/*
  GovernanceDashboard — Phase G one-screen daily view for the
  clinical lead. Wraps four widgets fed by GET /api/governance/dashboard:

    · Onboarding worklist — oldest-first list of clients with
      pending canonical fields; safety-flagged rows highlighted.
    · Safety alerts — non-finalised assessments with unacknowledged
      critical rule hits (spec §4.3).
    · Outcome trends — count + avg per pathway over the selected
      window (Onero readiness, GLA:D pain, PDQ-39 total, MSK pain).
    · Transcript-error corpus — bucketed reasons + top offending
      fields from the ``transcript_suggestion_feedback`` collection
      (spec §11 corpus we ship into prompt tuning).

  Purely a governance surface — no writes. The clinical lead uses
  the row-level links to drill through into the underlying record
  (assessment editor, client onboarding review).
*/
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ClipboardCheck, AlertTriangle, TrendingUp, MessageSquareWarning,
  RefreshCcw, Sparkles, ArrowRight,
} from "lucide-react";
import PathwayAllowListWidget from "@/components/PathwayAllowListWidget";
import GovernanceConfigWidget from "@/components/GovernanceConfigWidget";
import GovernanceAuditLogWidget from "@/components/GovernanceAuditLogWidget";


function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined,
      { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso.slice(0, 10); }
}

function fmtRelative(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const days = Math.floor(ms / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  return months === 1 ? "1 mo ago" : `${months} mo ago`;
}


export default function GovernanceDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [trendWeeks, setTrendWeeks] = useState(8);
  const [feedbackDays, setFeedbackDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/governance/dashboard", {
        params: {
          onboarding_limit: 25,
          alerts_limit: 25,
          trend_weeks: trendWeeks,
          feedback_days: feedbackDays,
        },
      });
      setData(r.data);
    } catch (e) {
      toast.error("Governance dashboard unavailable", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setLoading(false); }
  }, [trendWeeks, feedbackDays]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 space-y-6" data-testid="governance-dashboard">
      {/* Header ---------------------------------------------------- */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-violet-600" />
            Governance
          </h1>
          <p className="text-sm text-zinc-600 mt-1">
            Daily one-screen view for the clinical lead — onboarding review,
            safety alerts, outcome trends, and the transcript-AI feedback corpus.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data?.generated_at && (
            <span className="text-xs text-zinc-500">
              refreshed {fmtRelative(data.generated_at)}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="governance-refresh-btn"
          >
            <RefreshCcw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <OnboardingWidget data={data?.onboarding} loading={loading} />
        <SafetyAlertsWidget data={data?.safety_alerts} loading={loading} onRefresh={load} />
        <OutcomeTrendsWidget
          data={data?.outcome_trends} loading={loading}
          weeks={trendWeeks} setWeeks={setTrendWeeks}
        />
        <TranscriptFeedbackWidget
          data={data?.transcript_feedback} loading={loading}
          days={feedbackDays} setDays={setFeedbackDays}
        />
      </div>

      <PathwayAllowListWidget />
      <GovernanceConfigWidget />
      <GovernanceAuditLogWidget />
    </div>
  );
}


function WidgetShell({ icon: Icon, title, tone = "violet", children, headerRight }) {
  const toneMap = {
    violet: "border-violet-200 bg-violet-50 text-violet-900",
    rose:   "border-rose-200   bg-rose-50   text-rose-900",
    emerald:"border-emerald-200 bg-emerald-50 text-emerald-900",
    amber:  "border-amber-200  bg-amber-50  text-amber-900",
  };
  return (
    <Card className="p-4 flex flex-col">
      <div className={`flex items-center gap-2 border rounded-md px-3 py-2 mb-3 ${toneMap[tone]}`}>
        <Icon className="h-4 w-4 shrink-0" />
        <div className="font-semibold text-sm">{title}</div>
        <div className="ml-auto">{headerRight}</div>
      </div>
      <div className="flex-1 min-h-0">{children}</div>
    </Card>
  );
}


/* -------------------------------- 1. Onboarding worklist */

function OnboardingWidget({ data, loading }) {
  const total = data?.total_clients_pending ?? 0;
  const items = data?.items || [];
  return (
    <WidgetShell
      icon={ClipboardCheck}
      title={`Onboarding worklist — ${total} client${total === 1 ? "" : "s"} pending review`}
      tone="violet"
    >
      {loading && !items.length && <div className="text-sm text-zinc-500">Loading…</div>}
      {!loading && !items.length && (
        <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-3">
          ✓ Every client's onboarding data has been reviewed.
        </div>
      )}
      <ul className="divide-y divide-zinc-100">
        {items.map((r) => (
          <li key={r.client_email} className="py-2 flex items-center gap-2"
              data-testid={`gov-onboarding-row-${r.client_email}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-zinc-900 truncate">
                {r.client_name || r.client_email}
              </div>
              <div className="text-2xs text-zinc-500">
                first seen {fmtDate(r.first_seen_at)} · {fmtRelative(r.first_seen_at)}
              </div>
            </div>
            {r.safety_pending > 0 && (
              <span className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border border-rose-300 bg-rose-100 text-rose-900"
                    title="Includes at least one safety-critical answer awaiting review">
                {r.safety_pending} safety
              </span>
            )}
            <span className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border border-violet-300 bg-violet-100 text-violet-900">
              {r.pending_count} pending
            </span>
            <Link
              to={`/clients?email=${encodeURIComponent(r.client_email)}`}
              className="text-2xs text-violet-800 hover:text-violet-900 inline-flex items-center"
              data-testid={`gov-onboarding-open-${r.client_email}`}
            >
              Open <ArrowRight className="h-3 w-3 ml-0.5" />
            </Link>
          </li>
        ))}
      </ul>
    </WidgetShell>
  );
}


/* -------------------------------- 2. Safety alerts */

function SafetyAlertsWidget({ data, loading, onRefresh }) {
  const total = data?.total_assessments_with_alerts ?? 0;
  const items = data?.items || [];

  async function ackAll(row) {
    try {
      // Acknowledge every alert on the row; the endpoint is idempotent.
      for (const al of row.alerts) {
        await api.post(`/assessments/${row.assessment_id}/acknowledge`, {
          rule_key: al.key,
        });
      }
      toast.success("Alerts acknowledged");
      await onRefresh?.();
    } catch (e) {
      toast.error("Acknowledge failed", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }

  return (
    <WidgetShell
      icon={AlertTriangle}
      title={`Safety alerts — ${total} assessment${total === 1 ? "" : "s"} unresolved`}
      tone="rose"
    >
      {loading && !items.length && <div className="text-sm text-zinc-500">Loading…</div>}
      {!loading && !items.length && (
        <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-3">
          ✓ No unacknowledged critical alerts firing.
        </div>
      )}
      <ul className="divide-y divide-zinc-100">
        {items.map((r) => (
          <li key={r.assessment_id} className="py-2 space-y-1"
              data-testid={`gov-alerts-row-${r.assessment_id}`}>
            <div className="flex items-center gap-2">
              <div className="text-sm font-medium text-zinc-900 flex-1 truncate">
                {r.client_name || r.client_email || "Unknown client"}
              </div>
              <span className="text-2xs text-zinc-500">
                {fmtRelative(r.started_at)}
              </span>
              <button
                type="button"
                onClick={() => ackAll(r)}
                className="text-2xs px-2 py-0.5 rounded border border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-50"
                title="Acknowledge every firing alert on this assessment"
                data-testid={`gov-alerts-ack-${r.assessment_id}`}
              >
                Acknowledge
              </button>
              <Link
                to={`/assessments/${r.assessment_id}`}
                className="text-2xs text-rose-800 hover:text-rose-900 inline-flex items-center"
                data-testid={`gov-alerts-open-${r.assessment_id}`}
              >
                Review <ArrowRight className="h-3 w-3 ml-0.5" />
              </Link>
            </div>
            <div className="flex flex-wrap gap-1">
              {r.alerts.map((al) => (
                <span
                  key={al.key}
                  className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border border-rose-300 bg-rose-100 text-rose-900"
                  title={al.field}
                >
                  {al.title}
                  {al.stale && <span className="ml-1 opacity-60">(stale)</span>}
                </span>
              ))}
            </div>
          </li>
        ))}
      </ul>
    </WidgetShell>
  );
}


/* -------------------------------- 3. Outcome trends */

function OutcomeTrendsWidget({ data, loading, weeks, setWeeks }) {
  const items = data?.items || [];
  return (
    <WidgetShell
      icon={TrendingUp}
      title={`Outcome trends — last ${weeks} weeks`}
      tone="emerald"
      headerRight={
        <select
          value={weeks}
          onChange={(e) => setWeeks(Number(e.target.value))}
          className="text-2xs bg-white border border-emerald-300 rounded px-1"
          data-testid="gov-trend-weeks-select"
        >
          {[4, 8, 12, 26, 52].map((w) => (
            <option key={w} value={w}>{w} wks</option>
          ))}
        </select>
      }
    >
      {loading && !items.length && <div className="text-sm text-zinc-500">Loading…</div>}
      {!loading && !items.length && (
        <div className="text-sm text-zinc-600 italic">
          Not enough finalised assessments in this window to compute trends yet.
        </div>
      )}
      <div className="grid gap-2">
        {items.map((t) => (
          <div key={t.pathway}
                className="p-2 border border-zinc-200 rounded flex items-center gap-3"
                data-testid={`gov-trend-row-${t.pathway}`}>
            <div className="text-sm font-medium text-zinc-900 w-24 shrink-0">
              {t.pathway}
            </div>
            <div className="flex-1">
              <div className="flex items-baseline gap-3 text-xs text-zinc-600">
                <span>n = <b className="text-zinc-900">{t.count}</b></span>
                <span>avg <b className="text-zinc-900">{t.average}</b></span>
                <span>min {t.min} · max {t.max}</span>
              </div>
              <div className="text-2xs uppercase tracking-wider text-zinc-500">
                {t.field_key} · {t.direction === "up" ? "higher = better" : "lower = better"}
              </div>
            </div>
          </div>
        ))}
      </div>
    </WidgetShell>
  );
}


/* -------------------------------- 4. Transcript-error corpus */

function TranscriptFeedbackWidget({ data, loading, days, setDays }) {
  const total = data?.total_reports ?? 0;
  const byReason = data?.by_reason || [];
  const topFields = data?.top_fields || [];
  const maxCount = useMemo(
    () => Math.max(1, ...byReason.map((r) => r.count)),
    [byReason],
  );
  return (
    <WidgetShell
      icon={MessageSquareWarning}
      title={`Transcript-AI feedback — ${total} report${total === 1 ? "" : "s"} in ${days}d`}
      tone="amber"
      headerRight={
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="text-2xs bg-white border border-amber-300 rounded px-1"
          data-testid="gov-feedback-days-select"
        >
          {[7, 14, 30, 60, 90].map((d) => (
            <option key={d} value={d}>{d} days</option>
          ))}
        </select>
      }
    >
      {loading && !total && <div className="text-sm text-zinc-500">Loading…</div>}
      {!loading && !total && (
        <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-3">
          ✓ No transcript errors reported in the last {days} days.
        </div>
      )}
      {total > 0 && (
        <div className="space-y-3">
          <div>
            <div className="text-2xs uppercase tracking-wider text-amber-900 font-semibold mb-1">
              By reason
            </div>
            <div className="space-y-1">
              {byReason.map((r) => (
                <div key={r.reason} className="flex items-center gap-2 text-xs">
                  <div className="w-32 shrink-0 capitalize">
                    {r.reason.replace("_", " ")}
                  </div>
                  <div className="flex-1 h-2 bg-amber-100 rounded relative overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 bg-amber-500 rounded"
                      style={{ width: `${(r.count / maxCount) * 100}%` }}
                    />
                  </div>
                  <div className="w-8 text-right font-mono-num text-zinc-800">
                    {r.count}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {topFields.length > 0 && (
            <div>
              <div className="text-2xs uppercase tracking-wider text-amber-900 font-semibold mb-1">
                Top offending fields
              </div>
              <ul className="text-xs space-y-0.5">
                {topFields.map((f) => (
                  <li key={f.field_label} className="flex items-center justify-between">
                    <span className="truncate text-zinc-900">{f.field_label}</span>
                    <span className="font-mono-num text-zinc-600 ml-2">{f.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <a
            href="/api/transcript-suggestions/feedback/export?limit=5000"
            target="_blank"
            rel="noreferrer"
            className="text-2xs text-amber-900 underline hover:text-amber-950"
            data-testid="gov-feedback-export-link"
          >
            Export corpus (JSONL, grep-friendly) →
          </a>
        </div>
      )}
    </WidgetShell>
  );
}
